import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import { recentProjects, elapsedSince } from '../../lib/timetrackerStats';

// Ring geometry: r=42 in a 96 viewBox -> circumference 2*PI*42.
export const RING_RADIUS = 42;
export const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const keyOf = tt => `${tt.client || ''}::${tt.project || ''}::${tt.work || ''}`;

/**
 * Frontpage card for the time tracker: every running timer as a big ring
 * tile (one lap of the ring per hour, live hh:mm:ss, the ring centre is the
 * stop button) plus a row of recent projects as play chips to start from.
 *
 * Ticking is a local 1 s interval only - no API calls. The parent still
 * refreshes the lists on its own schedule; toggling refreshes them via the
 * store.
 */
@observer
class TimeTrackerActiveTimers extends Component {
    constructor(props) {
        super(props);
        this.state = {
            now: new Date(),
            togglingKey: null,
        };
    }

    componentDidMount() {
        this.tickTimer = setInterval(() => this.setState({ now: new Date() }), 1000);
    }

    componentWillUnmount() {
        clearInterval(this.tickTimer);
    }

    toggleTimer = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { client, project, work, combokey } = e.target.closest('button').dataset;
        const { timeTrackerStore } = this.props.stores;
        this.setState({ togglingKey: combokey });
        try {
            await timeTrackerStore.toggleTimer({ client, project, work });
        } catch (err) {
            console.error('Toggle failed:', err);
        }
        this.setState({ togglingKey: null });
    }

    renderRunning = (tt, bgClass) => {
        const { now, togglingKey } = this.state;
        const key = keyOf(tt);
        const isToggling = togglingKey === key;
        const elapsed = elapsedSince(tt.startTime, now);
        const dashOffset = (RING_CIRCUMFERENCE * (1 - elapsed.hourFraction)).toFixed(2);
        const startedAt = tt.startTime ? util.formatDate(tt.startTime, {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            day: '2-digit',
            month: 'short',
            locale: 'nb-NO',
        }, true) : '';

        return (
            <div class={`tt-tile ${bgClass} text-body rounded-4 border border-secondary-subtle`} key={key}>
                <div class='tt-ring'>
                    <svg viewBox='0 0 96 96' aria-hidden='true'>
                        <circle class='tt-ring-track' cx='48' cy='48' r={RING_RADIUS} fill='none' stroke-width='6' />
                        <circle
                            class='tt-ring-arc'
                            cx='48'
                            cy='48'
                            r={RING_RADIUS}
                            fill='none'
                            stroke-width='6'
                            stroke-dasharray={RING_CIRCUMFERENCE.toFixed(2)}
                            stroke-dashoffset={dashOffset}
                        />
                    </svg>
                    <button
                        type='button'
                        class='tt-ring-stop'
                        data-client={tt.client}
                        data-project={tt.project}
                        data-work={tt.work}
                        data-combokey={key}
                        onClick={this.toggleTimer}
                        disabled={isToggling}
                    >
                        <span class='tt-hm'>{elapsed.hm}</span>
                        <span class='tt-ss'>{elapsed.ss} s</span>
                        <span class='tt-stop-label' aria-label='Stop'>
                            {isToggling
                                ? <i class='fa-duotone fa-spinner-third fa-spin' />
                                : <i class='fa-solid fa-stop' />}
                        </span>
                    </button>
                </div>
                <div class='tt-client fw-semibold'>
                    <span class='tt-dot' /> {tt.client || <Text id='timetracker.no-client'>No client</Text>}
                </div>
                <div class='tt-project text-body-secondary'>
                    {tt.project || ''}{tt.work ? ` / ${tt.work}` : ''}
                </div>
                {startedAt && <div class='tt-since text-body-tertiary'>
                    <Text id='timetracker.started-at'>Started</Text> {startedAt}
                </div>}
            </div>
        );
    }

    renderStartChip = (rp) => {
        const isToggling = this.state.togglingKey === rp.key;
        return (
            <button
                type='button'
                class='btn btn-sm btn-outline-secondary rounded-pill tt-chip'
                data-client={rp.client}
                data-project={rp.project}
                data-work={rp.work}
                data-combokey={rp.key}
                onClick={this.toggleTimer}
                disabled={isToggling}
                key={rp.key}
            >
                <span class='tt-chip-play'>
                    {isToggling ? <i class='fa-duotone fa-spinner-third fa-spin' /> : <i class='fa-solid fa-play' />}
                </span>
                <span class='fw-semibold'>{rp.client}</span>
                {(rp.project || rp.work) && <small class='text-body-secondary'>
                    {rp.project}{rp.work ? ` / ${rp.work}` : ''}
                </small>}
            </button>
        );
    }

    render() {
        const { timeTrackerStore, userStore } = this.props.stores;
        const { timeTrackers = [], classNames = '' } = this.props;
        const { runningTimers = [] } = timeTrackerStore;
        const { user = {} } = userStore || {};
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        // Same black/white box as the rest of the TimeTracker module - follows
        // the user's darkmode setting, not Bootstrap's gray body tints.
        const bgClass = darkmode ? 'bg-darkmode' : 'bg-lightmode';

        const running = runningTimers.filter(tt => tt && !tt.endTime);
        const startable = recentProjects(timeTrackers, running).filter(rp => !rp.isRunning);

        if (running.length === 0 && startable.length === 0) {
            return null;
        }

        return (
            <div class={`${classNames} tt-active-timers`}>
                <div class='font-weight-lighter px-3 box-header'>
                    <Text id='timetracker.title'>TimeTracker</Text>
                </div>
                <div class={`mx-3 px-3 py-3 rounded-lg ${bgClass}`}>
                    {running.length > 0 && <div class='tt-tiles'>
                        {running.map(tt => this.renderRunning(tt, bgClass))}
                    </div>}
                    {startable.length > 0 && <div class={running.length > 0 ? 'mt-4' : ''}>
                        <div class='d-flex flex-wrap gap-2'>
                            {startable.map(this.renderStartChip)}
                        </div>
                    </div>}
                </div>
            </div>
        );
    }
}

export default TimeTrackerActiveTimers;
